import { isAddress } from 'ethers/address';
import { Contract } from 'ethers/contract';
import { convertAbiToInterface } from './validation-utilities.js';
/**
 * Safely create an Ethers contract with validation and ABI cleaning
 * @template T - Contract type extending ethers.Contract
 * @param contractAddress - Contract address as string
 * @param contractAbi - Contract ABI (can be JSON string, array, or Interface)
 * @param providerOrSigner - Ethers provider or signer
 * @returns Typed ethers contract instance
 * @throws Error if inputs are invalid
 */
export function createSafeContract(contractAddress, contractAbi, providerOrSigner) {
    // Validate contract address
    if (!contractAddress || typeof contractAddress !== 'string') {
        throw new Error('Contract address is required and must be a string');
    }
    if (!isAddress(contractAddress)) {
        throw new Error(`Invalid contract address: ${contractAddress}`);
    }
    // Validate ABI
    if (!contractAbi) {
        throw new Error('Contract ABI is required');
    }
    // Validate provider/signer
    if (!providerOrSigner) {
        throw new Error('Provider or signer is required');
    }
    try {
        // Clean and convert ABI to Interface (handles indexed properties in function inputs)
        const cleanInterface = convertAbiToInterface(contractAbi);
        // Create contract with cleaned ABI
        return new Contract(contractAddress, cleanInterface, providerOrSigner);
    }
    catch (error) {
        throw new Error(`Failed to create contract: ${error.message}`);
    }
}
/**
 * Generic contract creation with signer for transaction execution
 * @template T - Contract type extending ethers.Contract
 * @param contractAddress - Contract address as string
 * @param contractAbi - Contract ABI (can be JSON string, array, or Interface)
 * @param signer - Ethers signer for transaction signing
 * @returns Typed ethers contract instance with signer attached
 */
export function createContractWithSigner(contractAddress, contractAbi, signer) {
    if (!isAddress(contractAddress)) {
        throw new Error(`Invalid contract address: ${contractAddress}`);
    }
    return new Contract(contractAddress, contractAbi, signer);
}
/**
 * Generic read-only contract creation for querying (no signing capability)
 * @template T - Contract type extending ethers.Contract
 * @param contractAddress - Contract address as string
 * @param contractAbi - Contract ABI (can be JSON string, array, or Interface)
 * @param provider - Ethers provider for read-only operations
 * @returns Typed ethers contract instance for queries only
 */
export function createContractReadOnly(contractAddress, contractAbi, provider) {
    if (!isAddress(contractAddress)) {
        throw new Error(`Invalid contract address: ${contractAddress}`);
    }
    return new Contract(contractAddress, contractAbi, provider);
}
/**
 * Generic contract state retrieval with graceful fallback handling
 * Handles both deployed and counterfactual contract scenarios
 * @template TState - Type of the expected state object
 * @param contract - Ethers contract instance
 * @param stateGetters - Object mapping state property names to contract method names
 * @param fallbackValues - Fallback values to use if contract calls fail
 * @param options - Configuration options
 * @returns Promise resolving to contract state object
 */
export async function getContractState(contract, stateGetters, fallbackValues, options = {}) {
    const { enableFallback = true, throwOnMissingFallback = true, logger } = options;
    try {
        const statePromises = Object.entries(stateGetters).map(async ([key, methodName]) => {
            const value = await contract[methodName]();
            return [key, value];
        });
        const stateEntries = await Promise.all(statePromises);
        return Object.fromEntries(stateEntries);
    }
    catch (contractError) {
        if (!enableFallback) {
            throw contractError;
        }
        logger?.warn(`Contract call failed, using fallback values: ${contractError.message}`);
        const result = {};
        for (const [key] of Object.entries(stateGetters)) {
            if (key in fallbackValues) {
                result[key] = fallbackValues[key];
            }
            else if (throwOnMissingFallback) {
                throw new Error(`Missing fallback value for state property: ${key}`);
            }
        }
        return result;
    }
}
/**
 * Checks if a contract/wallet is deployed on the blockchain
 * Useful for checking counterfactual wallet deployment status
 *
 * @param contractAddress - The contract/wallet address to check
 * @param provider - The ethers provider for blockchain queries
 * @returns True if the contract is deployed, false otherwise
 */
export async function isContractDeployed(contractAddress, provider) {
    try {
        if (!isAddress(contractAddress)) {
            throw new Error(`Invalid contract address: ${contractAddress}`);
        }
        const code = await provider.getCode(contractAddress);
        return code !== "0x";
    }
    catch (error) {
        // Return false if there's an error checking deployment status
        return false;
    }
}
/**
 * Checks if a contract ABI supports a specific function
 * @param contractAbi - Contract ABI array
 * @param functionName - Name of the function to check for
 * @returns True if the function exists in the ABI, false otherwise
 */
export function isFunctionalitySupported(contractAbi, functionName) {
    try {
        return !!contractAbi.getFunction(functionName);
    }
    catch {
        return false;
    }
}
/**
 * Ensures that a contract ABI supports a specific function (throws if not found)
 * @param contractAbi - Contract ABI array
 * @param functionName - Name of the function to check for
 * @throws Error if the function is not found in the ABI
 */
export function ensureFunctionalitySupported(contractAbi, functionName) {
    if (!isFunctionalitySupported(contractAbi, functionName)) {
        throw new Error(`The contract does not support the ${functionName} functionality.`);
    }
}
